import * as React from 'react';
import {useEffect, useState} from 'react';
import {ArrowPathIcon, PlusIcon, XMarkIcon} from "@heroicons/react/24/outline";

import {PrimaryButton} from '../../../../../components/general/PrimaryButton.tsx';
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import { GLASelector } from '../../../../../components/specific/GLASelector';
import { GlaFilterPreset } from '../../../../../components/specific/GLASelector/types';
import Form, { type FormInstance } from '../../../../../components/form-library';

import {Client} from "../../Client.ts";
import {DEFAULT_GL_ACCOUNT, GlAccount} from '../../../../../hooks/useGLAccounts.ts';

import {BlacklistRulePreviewTable} from "./BlacklistRulePreviewTable.tsx";
import {BlacklistRulePreviewTransaction, FormValues, NO_VAT, Rule, VatDirectionChoice} from "./types.ts";
import {buildRule} from "./logic.ts";
import {FieldRow} from "./FormComponents.tsx";
import { ComboboxList } from '../../../../../components/general/ComboboxList.tsx';
import FormDropdown from '../../../../../components/general/FormDropdown.tsx';
import Input from '../../../../../components/general/Input.tsx';
import { DOCUMENT_DIRECTION_LABELS } from '../../../../../types/enums.ts';
import { VatDirection, getVatCodeOptions } from '../../../../../types/vatCodes.ts';
import type { RuleOperatorEnum } from '../../../../../generated/enums.ts';

interface CreateBlacklistRuleContentProps {
    selectedBusiness: Client | null;
    onSelectedBusinessChange: (business: Client | null) => void;
    initialBusinessId?: string | null;
    initialRelationName?: string | null;
    initialRelationIban?: string | null;
    initialAmount?: number | null;
    initialDescription?: string | null;
    initialGeneralLedgerAccountId?: string | null;
    glAccounts: GlAccount[];
    glAccountsLoading: boolean;
    onClose: () => void;
    clients: Client[];
    onPreview: (rules: Rule[], clientBusinessId: string | null | undefined) => Promise<BlacklistRulePreviewTransaction[]>;
    onCreateRule: (rules: Rule[], glAccount: GlAccount | null, vatCode: string | null, vatDirection: string | null) => Promise<void>;
}

export function CreateBlacklistRuleContent({
                                               clients,
                                               glAccounts,
                                               glAccountsLoading,
                                               onSelectedBusinessChange,
                                               initialBusinessId = null,
                                               initialRelationName = null,
                                               initialRelationIban = null,
                                               initialAmount = null,
                                               initialDescription = null,
                                               initialGeneralLedgerAccountId = null,
                                               onPreview,
                                               onCreateRule,
                                               onClose,
                                               selectedBusiness,
                                           }: CreateBlacklistRuleContentProps) {
    const [form] = Form.useForm<FormValues>();
    const [previewTransactions, setPreviewTransactions] = useState<Array<BlacklistRulePreviewTransaction> | null>(null);
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);

    useEffect(() => {
        const initialBusiness = initialBusinessId ? clients.find((c) => c.id === initialBusinessId) || selectedBusiness : selectedBusiness;

        const initialGlAccount = glAccounts.find(acc => acc.id === initialGeneralLedgerAccountId) ||
            glAccounts.find(acc => acc.id === DEFAULT_GL_ACCOUNT.id) ||
            DEFAULT_GL_ACCOUNT;

        const initialValues: FormValues = {
            business: initialBusiness,
            relations: initialRelationName
                ? [{name: initialRelationName, operator: 'EQUALS'}]
                : [{name: '', operator: 'EQUALS'}],
            relation_iban: initialRelationIban || undefined,
            booking_date_operator: 'EQUALS',
            amount: (initialAmount !== null && !Number.isNaN(initialAmount)) ? initialAmount : undefined,
            amount_operator: 'EQUALS',
            descriptions: initialDescription
                ? [{text: initialDescription, operator: 'CONTAINS'}]
                : [{text: '', operator: 'CONTAINS'}],
            gl_account: initialGlAccount,
            vat_direction: NO_VAT,
        };
        form.setFieldsValue(initialValues);

        if (initialBusiness) {
            onSelectedBusinessChange(initialBusiness);
        }
    }, []);

    useEffect(() => {
        if (!glAccounts || glAccounts.length === 0) return;

        const currentGlAccount = form.getFieldValue('gl_account');
        if (currentGlAccount && currentGlAccount.id !== DEFAULT_GL_ACCOUNT.id) return;

        if (initialGeneralLedgerAccountId) {
            const found = glAccounts.find(acc => acc.id === initialGeneralLedgerAccountId) || null;
            if (found) {
                form.setFieldValue('gl_account', found);
                return;
            }
        }

        const uncategorized = glAccounts.find(acc => acc.id === DEFAULT_GL_ACCOUNT.id) || DEFAULT_GL_ACCOUNT;
        form.setFieldValue('gl_account', uncategorized);
    }, [initialGeneralLedgerAccountId, glAccounts]);

    async function handlePreview() {
        const formData = form.getFieldsValue();
        try {
            const builtRules = buildRule(formData);
            if (builtRules.length === 0) return;

            setPreviewLoading(true);
            try {
                const transactions = await onPreview(builtRules, formData.business?.id);
                setPreviewTransactions(transactions);
            } finally {
                setPreviewLoading(false);
            }
        } catch (error: any) {
            alert(error.message);
        }
    }

    async function handleCreateRule() {
        const formData = form.getFieldsValue();
        try {
            const builtRules = buildRule(formData);
            if (builtRules.length === 0) return;

            const glAccount = formData.gl_account?.id === DEFAULT_GL_ACCOUNT.id ? null : formData.gl_account || null;
            const direction = formData.vat_direction ?? NO_VAT;
            const hasVat = direction !== NO_VAT;

            if (hasVat && !glAccount) {
                throw new Error('Kies een grootboekrekening om BTW toe te passen.');
            }

            await onCreateRule(
                builtRules,
                glAccount,
                hasVat ? (formData.vat_code ?? null) : null,
                hasVat ? direction : null,
            );
        } catch (error: any) {
            alert(error.message);
        }
    }

    const initialBusiness = initialBusinessId ? clients.find((c) => c.id === initialBusinessId) || selectedBusiness : selectedBusiness;

    return (
        <Form
            form={form}
            className="w-full h-full space-y-0"
            onValuesChange={(changedValues) => {
                if (changedValues.business !== undefined) {
                    onSelectedBusinessChange(changedValues.business);
                }
                if (changedValues.vat_direction !== undefined) {
                    const nextDirection = changedValues.vat_direction as VatDirectionChoice;
                    form.setFieldValue(
                        'vat_code',
                        nextDirection === NO_VAT
                            ? undefined
                            : getVatCodeOptions(nextDirection as VatDirection)[0].value,
                    );
                }
            }}
        >
            <HeaderSection/>

            <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3 flex flex-col gap-5">
                    <BusinessSelectionSection clients={clients} form={form}/>
                    <RelationSection form={form}/>
                    <IbanSection/>
                    <BookingDateSection form={form}/>
                    <AmountSection form={form}/>
                    <DescriptionSection form={form}/>
                    <GlAccountSection glAccounts={glAccounts} glAccountsLoading={glAccountsLoading} form={form}/>
                    <VatSection form={form}/>
                </div>

                <div className="md:w-2/3">
                    <SelectedTransactionSummary
                        initialBusiness={initialBusiness}
                        initialRelationName={initialRelationName}
                        initialRelationIban={initialRelationIban}
                        initialAmount={initialAmount}
                        initialDescription={initialDescription}
                    />

                    <PreviewPanel
                        loading={previewLoading}
                        transactions={previewTransactions}
                        onPreview={handlePreview}
                        glAccounts={glAccounts}
                    />

                    <div className="mt-4 flex justify-end w-full gap-2">
                        <PrimaryButton
                            onClick={handleCreateRule}
                            confirmation={{
                                title: "Weet je het zeker dat je een nieuwe automatisering wil aanmaken?",
                                description: "Automatiseringen voorkomen dat klanten onnodig om documenten worden gevraagd.",
                                acceptVerb: "Aanmaken"
                            }}
                            title="Opslaan"
                            className={"w-fit"}
                        />
                    </div>
                </div>
            </div>
        </Form>
    );
}

function HeaderSection() {
    return (
        <h2 className="weight-400 text-gray-800 text-sm mb-6">
            Door een nieuwe automatisering te creëren, kan je voorkomen dat je klanten onnodig om documenten worden
            gevraagd.
        </h2>
    );
}

function BusinessSelectionSection({clients, form}: { clients: Client[]; form: FormInstance<FormValues> }) {
    return (
        <div className="border-b border-gray-200 pb-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">Toepassen op</span>
            <Form.Item name="business" className="mb-0">
                {({ value }) => (
                    <ComboboxList<Client>
                        options={clients}
                        selected={value || null}
                        onSelect={(option) => {
                            form.setFieldValue('business', option);
                        }}
                        emptySelectionMessage="Selecteer een klant..."
                        internalMapper={(item) => ({ ...item, displayName: item.name, id: item.id })}
                        disabled={false}
                        showClearButton
                    />
                )}
            </Form.Item>
        </div>
    );
}

function RelationSection({form}: { form: FormInstance<FormValues> }) {
    return (
        <Form.Item name="relations">
            {(fieldData) => {
                const relations: NonNullable<FormValues['relations']> = fieldData.value || [{name: '', operator: 'EQUALS'}];

                const addRelation = () => {
                    const lastOperator: RuleOperatorEnum = relations.length > 0
                        ? relations[relations.length - 1].operator
                        : 'EQUALS';
                    form.setFieldValue('relations', [...relations, {name: '', operator: lastOperator}]);
                };

                const removeRelation = (index: number) => {
                    const newRelations = relations.filter((_: any, i: number) => i !== index);
                    form.setFieldValue('relations', newRelations.length > 0 ? newRelations : [{
                        name: '',
                        operator: 'EQUALS'
                    }] satisfies NonNullable<FormValues['relations']>);
                };

                return (
                    <div className="flex flex-col gap-2">
                        {relations.map((_: any, index: number) => (
                            <FieldRow key={index}>
                                <Form.Item name={['relations', index, 'operator']} label={index === 0 ? "Relatie" : undefined} className="mb-0 min-w-[10rem]">
                                    <FormDropdown
                                        options={[
                                            {value: 'CONTAINS', label: 'bevat'},
                                            {value: 'EQUALS', label: 'is gelijk aan'}
                                        ] satisfies Array<{ value: RuleOperatorEnum; label: string }>}
                                        placeholder="Selecteer..."
                                        searchable={false}
                                    />
                                </Form.Item>
                                <Form.Item name={['relations', index, 'name']} className="flex-1 mb-0">
                                    <Input />
                                </Form.Item>
                                <div className="flex mb-2 gap-1">
                                    {relations.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRelation(index)}
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                            title="Verwijder regel"
                                        >
                                            <XMarkIcon className="w-5 h-5"/>
                                        </button>
                                    )}
                                    {index === relations.length - 1 && (
                                        <button
                                            type="button"
                                            onClick={addRelation}
                                            className="p-1 text-gray-400 hover:text-primary transition-colors"
                                            title="Voeg regel toe"
                                        >
                                            <PlusIcon className="w-5 h-5"/>
                                        </button>
                                    )}
                                </div>
                            </FieldRow>
                        ))}
                    </div>
                );
            }}
        </Form.Item>
    );
}

function IbanSection() {
    return (
        <Form.Item name="relation_iban" label="Relatie IBAN">
            <Input />
        </Form.Item>
    );
}

function BookingDateSection({form}: { form: FormInstance<FormValues> }) {
    const operator = form.getFieldValue('booking_date_operator');
    return (
        <FieldRow>
            <Form.Item name="booking_date_operator" label="Datum is" className="min-w-[10rem]">
                <FormDropdown
                    options={[
                        {value: 'LESS_THAN', label: 'voor'},
                        {value: 'GREATER_THAN', label: 'na'},
                        {value: 'EQUALS', label: 'is gelijk aan'},
                        {value: 'BETWEEN', label: 'tussen'},
                    ] satisfies Array<{ value: RuleOperatorEnum; label: string }>}
                    placeholder="Selecteer..."
                    searchable={false}
                />
            </Form.Item>
            <Form.Item name="booking_date">
                <Input type="date" />
            </Form.Item>
            {operator === 'BETWEEN' && (
                <>
                    <span className="mb-2">en</span>
                    <Form.Item name="booking_date_upper">
                        <Input type="date" />
                    </Form.Item>
                </>
            )}
        </FieldRow>
    );
}

function AmountSection({form}: { form: FormInstance<FormValues> }) {
    const operator = form.getFieldValue('amount_operator');
    return (
        <FieldRow>
            <Form.Item name="amount_operator" label="Bedrag is" className="min-w-[10rem]">
                <FormDropdown
                    options={[
                        {value: 'LESS_THAN', label: 'kleiner dan'},
                        {value: 'GREATER_THAN', label: 'groter dan'},
                        {value: 'EQUALS', label: 'is gelijk aan'},
                        {value: 'BETWEEN', label: 'tussen'},
                    ] satisfies Array<{ value: RuleOperatorEnum; label: string }>}
                    placeholder="Selecteer..."
                    searchable={false}
                />
            </Form.Item>
            <Form.Item name="amount" className="flex-1">
                <Input type="number" inputMode="decimal" step="0.01" />
            </Form.Item>
            {operator === 'BETWEEN' && (
                <>
                    <span className="mb-2">en</span>
                    <Form.Item name="amount_upper" className="flex-1">
                        <Input type="number" inputMode="decimal" step="0.01" />
                    </Form.Item>
                </>
            )}
        </FieldRow>
    );
}

function DescriptionSection({form}: { form: FormInstance<FormValues> }) {
    return (
        <Form.Item name="descriptions">
            {(fieldData) => {
                const descriptions: NonNullable<FormValues['descriptions']> = fieldData.value || [{text: '', operator: 'CONTAINS'}];

                const addDescription = () => {
                    const lastOperator: RuleOperatorEnum = descriptions.length > 0
                        ? descriptions[descriptions.length - 1].operator
                        : 'CONTAINS';
                    form.setFieldValue('descriptions', [...descriptions, {text: '', operator: lastOperator}]);
                };

                const removeDescription = (index: number) => {
                    const newDescriptions = descriptions.filter((_: any, i: number) => i !== index);
                    form.setFieldValue('descriptions', newDescriptions.length > 0 ? newDescriptions : [{
                        text: '',
                        operator: 'CONTAINS'
                    }] satisfies NonNullable<FormValues['descriptions']>);
                };

                return (
                    <div className="flex flex-col gap-2">
                        {descriptions.map((_: any, index: number) => (
                            <FieldRow key={index}>
                                <Form.Item name={['descriptions', index, 'operator']} label={index === 0 ? "Omschrijving" : undefined} className="mb-0 min-w-[10rem]">
                                    <FormDropdown
                                        options={[
                                            {value: 'CONTAINS', label: 'bevat'},
                                            {value: 'EQUALS', label: 'is gelijk aan'}
                                        ] satisfies Array<{ value: RuleOperatorEnum; label: string }>}
                                        placeholder="Selecteer..."
                                        searchable={false}
                                    />
                                </Form.Item>
                                <Form.Item name={['descriptions', index, 'text']} className="flex-1 mb-0">
                                    <Input />
                                </Form.Item>
                                <div className="flex mb-2 gap-1">
                                    {descriptions.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeDescription(index)}
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                            title="Verwijder regel"
                                        >
                                            <XMarkIcon className="w-5 h-5"/>
                                        </button>
                                    )}
                                    {index === descriptions.length - 1 && (
                                        <button
                                            type="button"
                                            onClick={addDescription}
                                            className="p-1 text-gray-400 hover:text-primary transition-colors"
                                            title="Voeg regel toe"
                                        >
                                            <PlusIcon className="w-5 h-5"/>
                                        </button>
                                    )}
                                </div>
                            </FieldRow>
                        ))}
                    </div>
                );
            }}
        </Form.Item>
    );
}

function GlAccountSection({glAccounts, glAccountsLoading, form}: { glAccounts: GlAccount[]; glAccountsLoading: boolean; form: FormInstance<FormValues> }) {
    return (
        <div className="flex flex-col gap-1 w-full">
            <span className="block text-sm font-medium text-gray-700">Grootboekrekening</span>
            <Form.Item name="gl_account">
                {({ value }) => (
                    <GLASelector
                        accounts={glAccounts}
                        value={value ?? null}
                        onChange={(v) => form.setFieldValue('gl_account', v)}
                        filterPreset={GlaFilterPreset.ALL}
                        disabled={glAccountsLoading}
                    />
                )}
            </Form.Item>
        </div>
    );
}

function VatSection({form}: { form: FormInstance<FormValues> }) {
    const direction = (form.getFieldValue('vat_direction') as VatDirectionChoice) ?? NO_VAT;
    const showVatCode = direction !== NO_VAT;

    return (
        <div className="flex flex-col gap-2">
            <Form.Item name="vat_direction" label="BTW" className="mb-0 min-w-[10rem]">
                <FormDropdown
                    options={[
                        {value: NO_VAT, label: 'Geen BTW'},
                        {value: 'sales_invoice', label: DOCUMENT_DIRECTION_LABELS['sales_invoice']},
                        {value: 'purchase_invoice', label: DOCUMENT_DIRECTION_LABELS['purchase_invoice']},
                    ] satisfies Array<{ value: VatDirectionChoice; label: string }>}
                    placeholder="Selecteer..."
                    searchable={false}
                />
            </Form.Item>
            {showVatCode && (
                <Form.Item name="vat_code" label="BTW-tarief" className="mb-0 min-w-[10rem]">
                    <FormDropdown
                        options={getVatCodeOptions(direction as VatDirection)}
                        placeholder="Selecteer..."
                        searchable={false}
                    />
                </Form.Item>
            )}
        </div>
    );
}

function SelectedTransactionSummary({
                                        initialBusiness,
                                        initialRelationName,
                                        initialRelationIban,
                                        initialAmount,
                                        initialDescription
                                    }: {
    initialBusiness: Client | null,
    initialRelationName: string | null,
    initialRelationIban: string | null,
    initialAmount: number | null,
    initialDescription: string | null
}) {
    const hasInitialValues = initialBusiness || initialRelationName || initialRelationIban || (initialAmount !== null && !Number.isNaN(initialAmount)) || initialDescription;
    if (!hasInitialValues) return null;

    return (
        <div className="mb-4 border border-blue-100 bg-blue-50 rounded-md p-3 text-sm text-gray-800">
            <div className="font-semibold mb-2">Geselecteerde transactie</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
                {initialBusiness && (
                    <div><span className="font-medium">Klant: </span>{initialBusiness.name}</div>
                )}
                {initialRelationName && (
                    <div><span className="font-medium">Relatie: </span>{initialRelationName}</div>
                )}
                {initialRelationIban && (
                    <div><span className="font-medium">Relatie IBAN: </span>{initialRelationIban}</div>
                )}
                {initialAmount !== null && !Number.isNaN(initialAmount) && (
                    <div>
                        <span className="font-medium">Bedrag: </span>
                        €{initialAmount.toLocaleString("nl-NL", {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </div>
                )}
                {initialDescription && (
                    <div><span className="font-medium">Omschrijving: </span>{initialDescription}</div>
                )}
            </div>
        </div>
    );
}

function PreviewPanel({loading, transactions, onPreview, glAccounts}: {
    loading: boolean,
    transactions: BlacklistRulePreviewTransaction[] | null,
    onPreview: () => void,
    glAccounts: GlAccount[]
}) {
    return (
        <div className="border border-gray-200 rounded-md bg-white shadow-xs">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">
                    Preview Panel {transactions?.length ? `- ${transactions.length} transacties` : ''}
                </div>
                <SecondaryButton
                    onClick={onPreview}
                    title="Preview"
                    className="w-fit"
                    icon={ArrowPathIcon}
                />
            </div>
            <div className="p-3 max-h-[70vh] overflow-y-auto min-h-[300px]">
                {loading && <div className="text-sm text-gray-500">Laden van preview…</div>}

                {!loading && transactions === null && (
                    <div className="text-sm text-gray-500">
                        Geen preview beschikbaar. Stel filters in en klik op Preview.
                    </div>
                )}

                {!loading && transactions?.length === 0 && (
                    <div className="text-sm text-gray-500">Geen transacties gevonden.</div>
                )}

                {!loading && transactions && transactions.length > 0 && (
                    <BlacklistRulePreviewTable transactions={transactions} glAccounts={glAccounts}/>
                )}
            </div>
        </div>
    );
}